import { Controller } from '@hotwired/stimulus';
import SortableTable from '../vendors/sortable-table';

export default class extends Controller {
	connect() {
		this.filter();
	}

	filter() {
		const filter = document.querySelector('.js-filter');
		if (!filter) return;
		const filterTable = filter.querySelector('.js-filter-table');
		const filterDatatable = filter.querySelector('.js-filter-datatable');
		const filterForm = filter.querySelector('.js-filter-form');
		const filterInputs = filterForm.querySelectorAll('input');
		const valueFloorAreaMax = filterForm.querySelector('.js-filter-floor-area-max').dataset.max;
		const filterTotalCount = filter.querySelector('.js-filter-total-count');
		const filterClear = filter.querySelector('.js-filter-clear');

		if (filterDatatable) {
			new SortableTable(filterDatatable);
		}

		function debounce(fn, delay = 200) {
			let timeout;
			return function (...args) {
				clearTimeout(timeout);
				timeout = setTimeout(() => fn.apply(this, args), delay);
			};
		}

		const handleFilterChange = debounce(() => {
			updateFilterTable();
			updateUrlParams();
		}, 150);

		filterInputs.forEach((input) => {
			input.addEventListener('change', handleFilterChange);
			input.addEventListener('input', handleFilterChange);
		});

		filterForm.addEventListener('submit', (e) => {
			e.preventDefault();
			handleFilterChange();
		});

		const encodeValue = (value) => {
			return encodeURIComponent(value).replace(/%20/g, '+').replace(/%2C/g, ',').replace(/%2B/g, '+');
		};

		const normalizeName = (name) => {
			return name.endsWith('[]') ? name.slice(0, -2) : name;
		};

		const updateUrlParams = () => {
			const tempValues = {};

			filterInputs.forEach((input) => {
				if (!input.name) return;

				const name = normalizeName(input.name);
				if (input.type === 'checkbox') {
					if (input.checked && input.value.trim() !== '') {
						if (!tempValues[name]) {
							tempValues[name] = [];
						}
						tempValues[name].push(input.value);
					}
				} else {
					if (input.value) {
						tempValues[name] = input.value;
					}
				}
			});

			const queryParts = [];

			Object.entries(tempValues).forEach(([key, value]) => {
				if (Array.isArray(value)) {
					queryParts.push(`${encodeURIComponent(key)}=${value.map(encodeValue).join(',')}`);
				} else {
					queryParts.push(`${encodeURIComponent(key)}=${encodeValue(value)}`);
				}
			});

			const queryString = queryParts.length ? `?${queryParts.join('&')}` : '';
			const newUrl = `${window.location.pathname}${queryString}`;
			history.replaceState(null, '', newUrl);

			const priceListLinks = document.querySelectorAll('.js-price-list-url');
			priceListLinks.forEach((link) => {
				const originalUrl = new URL(link.getAttribute('href'), window.location.origin);
				originalUrl.search = queryString;
				link.setAttribute('href', originalUrl.toString());
			});
		};

		const populateFormFromUrl = () => {
			const params = new URLSearchParams(window.location.search);

			params.forEach((rawValue, key) => {
				const decodedKey = decodeURIComponent(key);
				const decodedRawValue = decodeURIComponent(rawValue);
				const values = decodedRawValue.split(',').map((v) => v.trim());

				const matchingInputs = filterForm.querySelectorAll(`[name="${decodedKey}"], [name="${decodedKey}[]"]`);

				matchingInputs.forEach((input) => {
					if (input.type === 'checkbox') {
						input.checked = values.includes(input.value) || values.includes(input.value.replace(/\+/g, ' '));
					} else {
						input.value = decodedRawValue;
					}
				});

				const emptyCheckbox = [...matchingInputs].find((input) => input.type === 'checkbox' && input.value === '');
				if (emptyCheckbox) {
					const nonEmptyCheckboxes = [...matchingInputs].filter((input) => input.type === 'checkbox' && input.value !== '');
					emptyCheckbox.checked = nonEmptyCheckboxes.every((input) => values.includes(input.value));
				}
			});
		};

		populateFormFromUrl();

		const updateFilterTable = () => {
			const rows = filterTable.querySelectorAll('tr[data-filter]');
			const data = getFormData(filterForm);
			const filterBuilding = data.building ?? [];
			const filterDisposition = data.disposition ?? [];
			const filterFloor = data.floor ?? [];
			const filterOther = data.other || [];
			const filterAvailability = !!data.availability;
			const filterAreaMin = parseFloat(data.floor_area_from ?? 0);
			const filterAreaMax = parseFloat(data.floor_area_to ?? valueFloorAreaMax);
			const filterPriceMax = parseFloat(data.price_max);

			let totalCount = 0;
			let visibleIndex = 0;
			rows.forEach((row) => {
				const building = row.dataset.filterBuilding;
				const disposition = row.dataset.filterDisposition;
				const floor = row.dataset.filterFloor;
				const availability = row.dataset.filterAvailability;
				const price = parseFloat(row.dataset.filterPrice);
				const other = row.dataset.filterOther?.split(',').map((val) => val.trim()) || [];
				const matchBuilding = filterBuilding.length === 0 || filterBuilding.includes(building);
				const matchDisposition = filterDisposition.length === 0 || filterDisposition.includes(disposition);
				const matchFloor = filterFloor.length === 0 || filterFloor.includes(floor);
				const area = parseFloat(row.dataset.filterFloorArea);
				const matchAvailability = (filterAvailability && availability > 0) || !filterAvailability;
				const matchOther = filterOther.length === 0 || filterOther.some((val) => other.includes(val));

				const matchArea = isNaN(area) || (area >= filterAreaMin && area <= filterAreaMax);
				const matchPrice = isNaN(price) || price <= filterPriceMax;

				let isVisible =
					matchBuilding && matchDisposition && matchPrice && matchArea && matchAvailability && matchOther && matchFloor;

				if (isVisible) {
					row.classList.remove('u-d-n');
					row.classList.remove('is-odd');
					if (visibleIndex % 2 === 0) {
						row.classList.add('is-odd');
					}
					visibleIndex++;
					totalCount++;
				} else {
					row.classList.add('u-d-n');
					row.classList.remove('is-odd');
				}
			});

			filterTotalCount.innerHTML = totalCount;
		};

		function getFormData(form) {
			const formData = new FormData(form);
			const result = {};

			for (const [name, value] of formData.entries()) {
				if (!value.trim()) continue;

				if (name.endsWith('[]')) {
					const key = name.slice(0, -2);
					if (!result[key]) result[key] = [];
					result[key].push(value);
				} else {
					result[name] = value;
				}
			}

			return result;
		}

		const toggleAllCheckboxes = () => {
			const allCheckboxes = document.querySelectorAll('input[type="checkbox"][value=""]');

			allCheckboxes.forEach((allCheckbox) => {
				const fieldset = allCheckbox.closest('fieldset');
				if (!fieldset) return;

				const groupCheckboxes = fieldset.querySelectorAll('input[type="checkbox"]:not([value=""])');

				allCheckbox.addEventListener('change', () => {
					const isChecked = allCheckbox.checked;
					groupCheckboxes.forEach((cb) => {
						cb.checked = isChecked;
					});
				});

				groupCheckboxes.forEach((cb) => {
					cb.addEventListener('change', () => {
						allCheckbox.checked = Array.from(groupCheckboxes).every((cb) => cb.checked);
					});
				});
			});
		};
		const inputsNumber = () => {
			const inputs = document.querySelectorAll('input[name^="floor_area_"]');

			inputs.forEach((input) => {
				input.addEventListener('input', () => {
					input.value = input.value
						.replace(',', '.')
						.replace(/[^0-9.]/g, '')
						.replace(/(\..*)\./g, '$1');
				});
			});
		};
		const formClear = () => {
			filterClear.addEventListener('click', () => {
				const inputs = filterForm.querySelectorAll('input');

				inputs.forEach((input) => {
					if (input.type === 'checkbox') {
						input.checked = false;
					} else if (input.type === 'range') {
						input.value = input.max;
					} else {
						input.value = '';
					}
				});

				history.replaceState(null, '', window.location.pathname);

				updateFilterTable();
			});
		};

		updateFilterTable();
		toggleAllCheckboxes();
		inputsNumber();
		formClear();
	}
}
